// ProfileActionButtons.jsx
import React from 'react';
import {
  IconButton,
  Tooltip,
  Box,
  Typography
} from "@mui/material";
import {
  Send as SendIcon,
  GroupAdd as GroupAddIcon,
  Event as EventIcon,
  Email as EmailIcon,
  Group as GroupIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

/**
 * ProfileActionButtons Component
 * A reusable component that displays interactive action buttons for user profiles
 * 
 * @param {Object} props
 * @param {Object} props.user - The user object for the profile being viewed
 * @param {boolean} props.isOwnProfile - Whether the current user is viewing their own profile
 * @param {Function} props.setSuggestDialogOpen - Function to open suggest dialog
 * @param {Function} props.setInviteToConsortiumDialogOpen - Function to open invite to consortium dialog
 * @param {Function} props.setMeetingDialogOpen - Function to open meeting dialog
 * @param {Object} [props.sx] - Additional sx styles
 * @param {boolean} [props.showLabels=false] - Whether to show text labels instead of icons only
 * @param {boolean} [props.invertColors=false] - Whether to use colored icons without backgrounds (true = no bg, colored icons)
 * @param {string} [props.size="medium"] - Button size (xs, small, medium, large)
 * @param {string} [props.fontSize='1.5rem'] - Icon font size
 * @param {string[]} [props.only] - Just these actions ('suggest' | 'invite' | 'meet' | 'message'); all when left out
 */
const ActionButtons = ({
  user,
  isOwnProfile,
  setSuggestDialogOpen,
  setInviteToConsortiumDialogOpen,
  setMeetingDialogOpen,
  size = "medium",
  sx = {},
  showLabels = false,
  invertColors = false,
  fontSize = '1.5rem',
  only,
}) => {
  const shows = (key) => !only || only.includes(key);
  const navigate = useNavigate();
  
  // Don't show buttons for own profile
  if (!user || isOwnProfile) {
    return null;
  }

  const handleSuggest = () => {
    setSuggestDialogOpen(true);
  };

  const handleInvite = () => { 
    setInviteToConsortiumDialogOpen(true);
  };

  const handleMeet = () => {
    setMeetingDialogOpen(true);
  };

  const handleMessage = () => {
    navigate(`/messages/new?user=${user.id}`);
  };

  const p = size === "small" ? 0.7 : size === "xs" ? 0.5 : 1;
  
  // Determine styling based on invertColors prop
  const getButtonStyles = (colorType) => {
    if (invertColors) {
      // No background, colored icons
      return {
        p: p,
        color: colorType === 'suggest' ? '#ad89ad' :
               colorType === 'invite' ? 'primary.main' :
               colorType === 'meet' ? '#2196f3' :
               '#9e9e9e',
        '&:hover': {
          bgcolor: 'action.hover',
          color: colorType === 'suggest' ?  '#896b89' :
                 colorType === 'invite' ? 'primary.dark' :
                 colorType === 'meet' ? '#1976d2' :
                 '#757575',
        }
      };
    } else {
      // Original with colored backgrounds
      return {
        p: p,
        bgcolor: colorType === 'suggest' ? '#ad89ad' :
                 colorType === 'invite' ? 'primary.main' :
                 colorType === 'meet' ? '#2196f3' :
                 '#9e9e9e',
        color: 'white',
        '&:hover': {
          bgcolor: colorType === 'suggest' ?  '#896b89' :
          
                   colorType === 'invite' ? 'primary.dark' :
                   colorType === 'meet' ? '#1976d2' :
                   '#757575',
        }
      };
    }
  };

  // Icon-only version (default)
  if (!showLabels) {
    return (
      <Box display="flex" alignItems="center" gap={1} sx={sx} ml={.5}>
        {/* Suggest Button */}
        {shows('suggest') && (
        <Tooltip title="Suggest">
          <IconButton 
            size={size}
            onClick={(e) => {
              e.stopPropagation();
              handleSuggest();
            }}
            aria-label="Suggest"
            sx={getButtonStyles('suggest')}
          >
            <SendIcon sx={{ fontSize: fontSize }}/>
          </IconButton>
        </Tooltip>
        )}

        {/* Invite to Consortium Button */}
        {shows('invite') && (
        <Tooltip title="Invite to Consortium">
          <IconButton 
            size={size}
            onClick={(e) => {
              e.stopPropagation();
              handleInvite();
            }}
            aria-label="Invite to Consortium"
            sx={getButtonStyles('invite')}
          >
            <GroupAddIcon sx={{ fontSize: fontSize }}/>
          </IconButton>
        </Tooltip>
        )}

        {/* Schedule Meeting Button */}
        {shows('meet') && (
        <Tooltip title="Schedule Meeting">
          <IconButton 
            size={size}
            onClick={(e) => {
              e.stopPropagation();
              handleMeet();
            }}
            aria-label="Schedule Meeting"
            sx={getButtonStyles('meet')}
          >
            <EventIcon sx={{ fontSize: fontSize }}/>
          </IconButton>
        </Tooltip>
        )}

        {/* Send Message Button */}
        {shows('message') && (
        <Tooltip title="Send Message">
          <IconButton 
            size={size}
            onClick={(e) => {
              e.stopPropagation();
              handleMessage();
            }}
            aria-label="Send Message"
            sx={getButtonStyles('message')}
          >
            <EmailIcon sx={{ fontSize: fontSize }}/>
          </IconButton>
        </Tooltip>
        )}
      </Box>
    );
  }

  // Text button version (with labels) - also supports invertColors
  const getTextButtonStyles = (colorType) => {
    if (invertColors) {
      // No background, colored text
      return {
        color: colorType === 'suggest' ? 'secondary.main' :
               colorType === 'invite' ? 'primary.main' :
               colorType === 'meet' ? '#2196f3' :
               '#9e9e9e',
        borderRadius: '20px',
        px: 2,
        py: 1,
        '&:hover': {
          bgcolor: 'action.hover',
          color: colorType === 'suggest' ? 'secondary.dark' :
                 colorType === 'invite' ? 'primary.dark' :
                 colorType === 'meet' ? '#1976d2' :
                 '#757575',
        }
      };
    } else {
      // Original with colored backgrounds
      return {
        bgcolor: colorType === 'suggest' ? 'secondary.main' :
                 colorType === 'invite' ? 'primary.main' :
                 colorType === 'meet' ? '#2196f3' :
                 '#9e9e9e',
        color: 'white',
        borderRadius: '20px',
        px: 2,
        py: 1,
        '&:hover': {
          bgcolor: colorType === 'suggest' ? 'secondary.dark' :
                   colorType === 'invite' ? 'primary.dark' :
                   colorType === 'meet' ? '#1976d2' :
                   '#757575',
        }
      };
    }
  };

  return (
    <Box display="flex" alignItems="center" gap={2} sx={sx}>
      {/* Suggest Button with Text */}
      <Tooltip title="Suggest a collaboration or resource">
        <IconButton 
          size={size}
          onClick={(e) => {
            e.stopPropagation();
            handleSuggest();
          }}
          sx={{...getTextButtonStyles('suggest'), fontSize: fontSize}}
          fontSize={fontSize}
        >
          <SendIcon sx={{ mr: .5, fontSize: fontSize }} />
          Suggest
        </IconButton>
      </Tooltip>

      {/* Invite to Consortium Button with Text */}
      <Tooltip title="Invite to join a consortium">
        <IconButton 
          size={size}
          onClick={(e) => {
            e.stopPropagation();
            handleInvite();
          }}
          sx={{...getTextButtonStyles('invite'), fontSize: fontSize}}
         
        
        >
          <GroupAddIcon sx={{ mr: .5, fontSize: fontSize }} />
          Invite
        </IconButton>
      </Tooltip>

      {/* Schedule Meeting Button with Text */}
      <Tooltip title="Schedule a meeting">
        <IconButton 
          size={size}
          onClick={(e) => {
            e.stopPropagation();
            handleMeet();
          }}
          sx={{...getTextButtonStyles('meet'), fontSize: fontSize}}
          fontSize={fontSize}
        >
          <EventIcon sx={{ mr: .5, fontSize: fontSize }} />
          Meet
        </IconButton>
      </Tooltip>

      {/* Send Message Button with Text */}
      <Tooltip title="Send a direct message">
        <IconButton 
          size={size}
          onClick={(e) => {
            e.stopPropagation();
            handleMessage();
          }}
          sx={{...getTextButtonStyles('message'), fontSize: fontSize}}
          fontSize={fontSize}
        >
          <EmailIcon sx={{ mr: .5, fontSize: fontSize }} />
          Message
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// Individual button components with invertColors support

// Shared size config for all individual buttons
const getSizeConfig = (size) => {
  switch (size) {
    case 'xs':    return { p: 0.1, iconSize: '0.85rem' };
    case 'small': return { p: 0.3, iconSize: '1rem' };
    case 'large': return { p: 1, iconSize: '1.6rem' };
    case 'medium':
    default:      return { p: 0.7,   iconSize: '1.25rem' };
  }
};

export const SuggestButton = ({ 
  onClick, 
  sx = {}, 
  showLabel = false, 
  size = "medium", 
  fontSize,
  invertColors = false 
}) => {
  const { p, iconSize } = getSizeConfig(size);
  const resolvedFontSize = fontSize || iconSize;
  
  const buttonStyles = invertColors ? {
    p: p,
    color: 'secondary.main',
    '&:hover': {
      bgcolor: 'action.hover',
      color: 'secondary.dark',
    },
    ...(showLabel && {
      borderRadius: '20px',
      px: 2,
      py: 1
    }),
    ...sx
  } : {
    p: p,
    bgcolor: 'secondary.main',
    color: 'white',
    '&:hover': { bgcolor: 'secondary.dark' },
    ...(showLabel && {
      borderRadius: '20px',
      px: 2,
      py: 1
    }),
    ...sx
  };
  
  return (
    <Tooltip title="Suggest">
      <IconButton 
        onClick={onClick}
        sx={buttonStyles}
        size={size}
      >
        <SendIcon sx={showLabel ? { mr: 1, fontSize: resolvedFontSize } : { fontSize: resolvedFontSize }} />
        {showLabel && 'Suggest'}
      </IconButton>
    </Tooltip>
  );
};

export const InviteButton = ({ 
  onClick, 
  sx = {}, 
  showLabel = false, 
  size = "medium", 
  fontSize,
  invertColors = false 
}) => {
  const { p, iconSize } = getSizeConfig(size);
  const resolvedFontSize = fontSize || iconSize;

  const buttonStyles = invertColors ? {
    p: p,
    color: 'primary.main',
    '&:hover': {
      bgcolor: 'action.hover',
      color: 'primary.dark',
    },
    ...(showLabel && {
      borderRadius: '20px',
      px: 2,
      py: 1
    }),
    ...sx
  } : {
    p: p,
    bgcolor: 'primary.main',
    color: 'white',
    '&:hover': { bgcolor: 'primary.dark' },
    ...(showLabel && {
      borderRadius: '20px',
      px: 2,
      py: 1
    }),
    ...sx
  };
  
  return (
    <Tooltip title="Invite to Consortium">
      <IconButton 
        onClick={onClick}
        sx={buttonStyles}
        size={size}
      >
        <GroupAddIcon sx={showLabel ? { mr: 1, fontSize: resolvedFontSize } : { fontSize: resolvedFontSize }} />
        {showLabel && 'Invite'}
      </IconButton>
    </Tooltip>
  );
};


export const JoinButton = ({ 
  onClick, 
  sx = {}, 
  showLabel = false, 
  size = "medium", 
  fontSize,
  invertColors = false,
  isPending = false 
}) => {
  const { p, iconSize } = getSizeConfig(size);
  const resolvedFontSize = fontSize || iconSize;

  const buttonStyles = invertColors ? {
    p: p,
    color: 'primary.main',
    '&:hover': {
      bgcolor: 'action.hover',
      color: 'primary.dark',
    },
    '&.Mui-disabled': {
      color: 'action.disabled',
      bgcolor: 'action.disabledBackground'
    },
    ...(showLabel && {
      borderRadius: '20px',
      px: 2,
      py: 1
    }),
    ...sx
  } : {
    p: p,
    bgcolor: 'primary.main',
    color: 'white',
    '&:hover': { bgcolor: 'primary.dark' },
    '&.Mui-disabled': {
      bgcolor: 'action.disabledBackground',
      color: 'action.disabled'
    },
    ...(showLabel && {
      borderRadius: '20px',
      px: 2,
      py: 1
    }),
    ...sx
  };
  
  return (
    <Box display="flex" flexDirection="row" alignItems="center" gap={1} sx={sx}>
    <Tooltip title={isPending ? "Pending..." : "Join Consortium"}>
      <IconButton 
        onClick={onClick}
        sx={buttonStyles}
        size={size}
        disabled={isPending}
      >
        <GroupIcon sx={{ fontSize: resolvedFontSize }} />
        {showLabel && (isPending ? 'Pending' : 'Join')}
      </IconButton>
    </Tooltip>
    
    {isPending && (
      <Typography variant="body2" color="textSecondary">
        Pending...
      </Typography>
    )}
  </Box>

  );
};


export const MeetButton = ({ 
  onClick, 
  sx = {}, 
  showLabel = false, 
  size = "medium", 
  fontSize,
  invertColors = false 
}) => {
  const { p, iconSize } = getSizeConfig(size);
  const resolvedFontSize = fontSize || iconSize;

  const buttonStyles = invertColors ? {
    p: p,
    color: '#2196f3',
    '&:hover': {
      bgcolor: 'action.hover',
      color: '#1976d2',
    },
    ...(showLabel && {
      borderRadius: '20px',
      px: 2,
      py: 1
    }),
    ...sx
  } : {
    p: p,
    bgcolor: '#2196f3',
    color: 'white',
    '&:hover': { bgcolor: '#1976d2' },
    ...(showLabel && {
      borderRadius: '20px',
      px: 2,
      py: 1
    }),
    ...sx
  };
  
  return (
    <Tooltip title="Schedule Meeting">
      <IconButton 
        onClick={onClick}
        sx={buttonStyles}
        size={size}
      >
        <EventIcon sx={showLabel ? { mr: 1, fontSize: resolvedFontSize } : { fontSize: resolvedFontSize }} />
        {showLabel && 'Meet'}
      </IconButton>
    </Tooltip>
  );
};

export const MessageButton = ({ 
  onClick, 
  sx = {}, 
  showLabel = false, 
  size = "medium", 
  fontSize,
  invertColors = false 
}) => {
  const { p, iconSize } = getSizeConfig(size);
  const resolvedFontSize = fontSize || iconSize;

  const buttonStyles = invertColors ? {
    p: p,
    color: '#9e9e9e',
    '&:hover': {
      bgcolor: 'action.hover',
      color: '#757575',
    },
    ...(showLabel && {
      borderRadius: '20px',
      px: 2,
      py: 1
    }),
    ...sx
  } : {
    p: p,
    bgcolor: '#9e9e9e',
    color: 'white',
    '&:hover': { bgcolor: '#757575' },
    ...(showLabel && {
      borderRadius: '20px',
      px: 2,
      py: 1
    }),
    ...sx
  };
  
  return (
    <Tooltip title="Send Message">
      <IconButton 
        onClick={onClick}
        sx={buttonStyles}
        size={size}
      >
        <EmailIcon sx={showLabel ? { mr: 1, fontSize: resolvedFontSize } : { fontSize: resolvedFontSize }} />
        {showLabel && 'Message'}
      </IconButton>
    </Tooltip>
  );
};

export default ActionButtons;